import { Component, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { ApiService } from '../../services/api/api.service';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Asset } from '../../interfaces/Asset';
import { AssetResponse } from '../../interfaces/AssetResponse';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../common/confirm-dialog/confirn-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';

@Component({
    selector: 'app-m-assets',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatTooltipModule,
        TranslocoModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './m-assets.component.html',
    styleUrl: './m-assets.component.scss',
})
export class MAssetsComponent {
    displayedColumns: string[] = [
        'name',
        'domain',
        'assetVerificationStatus',
        'created',
        'action',
    ];
    data: Asset[] = [];
    dataSource = new MatTableDataSource<Asset>(this.data);
    totalItems = 0;
    pageSizes = [10, 20, 50, 1000];
    isLoadingResults = true;
    isRateLimitReached = false;

    readonly dialog = inject(MatDialog);

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    // isToggled
    isToggled = false;

    constructor(
        public themeService: CustomizerSettingsService,
        private apiService: ApiService
    ) {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngAfterViewInit() {
        // If the user changes the sort order, reset back to the first page.
        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                switchMap(() => {
                    this.isLoadingResults = true;
                    return this.apiService
                        .getAssets(
                            this.paginator.pageIndex,
                            this.paginator.pageSize,
                            this.sort.active,
                            this.sort.direction
                        )
                        .pipe(catchError(() => observableOf(null)));
                }),
                map((data) => {
                    // Flip flag to show that loading has finished.
                    this.isLoadingResults = false;
                    //this.isRateLimitReached = data === null;

                    if (data === null) {
                        return [];
                    }

                    // Only refresh the result length if there is new data. In case of rate
                    // limit errors, we do not want to reset the paginator to zero, as that
                    // would prevent users from re-triggering requests.
                    this.totalItems = data.totalElements;
                    return data.assets;
                })
            )
            .subscribe((data) => (this.dataSource.data = data));
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Search Filter
    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Delete
    delete(id: string) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                action: 'delete',
                component: 'asset',
                content: 'content-delete',
                buttonText: {
                    ok: 'yes',
                    cancel: 'no',
                },
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                console.log('Delete Asset: ' + id);
                this.apiService.deleteAsset(id).subscribe({
                    next: (value: AssetResponse) => {
                        console.log(value);
                        this.isLoadingResults = true;
                        this.paginator.pageIndex = 0;
                        this.apiService
                            .getAssets(
                                this.paginator.pageIndex,
                                this.paginator.pageSize,
                                this.sort.active,
                                this.sort.direction
                            )
                            .subscribe((response: AssetResponse) => {
                                // Flip flag to show that loading has finished.
                                this.isLoadingResults = false;
                                if (response) {
                                    this.totalItems = response.totalElements;
                                    this.dataSource.data = response.assets;
                                }
                            });
                    },
                    error: (error) => {
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: error,
                                },
                            }
                        );
                    },
                });
            }
        });
    }
}
